import { mesCurto, moeda, moedaCompacta } from '../util/formato';

export interface PontoDaEvolucao {
  readonly mes: string;
  readonly rotulo: string;
  readonly recebido: number;
  readonly honorarios: number;
}

/**
 * Evolução dos recebimentos nos últimos seis meses.
 *
 * Área com linha, e não barras: seis meses formam uma SÉRIE TEMPORAL, e a
 * inclinação entre pontos é justamente o que interessa — "está subindo?". Barras
 * separadas obrigam a comparar alturas duas a duas.
 *
 * O último ponto é destacado porque é o mês corrente: é o número que a advogada
 * procura primeiro.
 *
 * Desenhado em SVG à mão, sem biblioteca de gráficos: são seis pontos e uma série.
 * Trazer uma dependência de 100 kB para isso seria desproporcional.
 */
export function EvolucaoRecebimentos({ pontos }: { pontos: readonly PontoDaEvolucao[] }) {
  if (pontos.length === 0) return null;

  const L = 640;
  const A = 190;
  // As margens laterais precisam caber METADE do rótulo do eixo, que é centralizado
  // no ponto. Com margem menor que isso, o primeiro e o último mês saem cortados —
  // e "fev/26" vira "ev/26".
  const margem = { topo: 30, direita: 30, base: 34, esquerda: 30 };
  const larguraUtil = L - margem.esquerda - margem.direita;
  const alturaUtil = A - margem.topo - margem.base;

  // O teto do eixo nunca é o próprio máximo: encostar a série no topo tira a
  // referência de "quanto ainda cabe".
  const maximo = Math.max(...pontos.map((p) => p.recebido), 1) * 1.18;

  const x = (i: number) =>
    margem.esquerda + (pontos.length === 1 ? larguraUtil / 2 : (i / (pontos.length - 1)) * larguraUtil);
  const y = (valor: number) => margem.topo + alturaUtil - (valor / maximo) * alturaUtil;

  const linha = pontos.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.recebido)}`).join(' ');
  const area = `${linha} L ${x(pontos.length - 1)} ${margem.topo + alturaUtil} L ${x(0)} ${
    margem.topo + alturaUtil
  } Z`;

  const ultimo = pontos.length - 1;

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${L} ${A}`}
        className="h-auto w-full"
        role="img"
        aria-label={`Recebimentos por mês. ${pontos
          .map((p) => `${p.rotulo}: ${moeda(p.recebido)}`)
          .join('. ')}`}
      >
        <defs>
          <linearGradient id="preenchimentoRecebido" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-recebido)" stopOpacity="0.20" />
            <stop offset="100%" stopColor="var(--color-recebido)" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Grade tênue: dá referência de altura sem virar gaiola. */}
        {[0, 0.5, 1].map((fracao) => (
          <line
            key={fracao}
            x1={margem.esquerda}
            x2={L - margem.direita}
            y1={margem.topo + alturaUtil * fracao}
            y2={margem.topo + alturaUtil * fracao}
            stroke="var(--borda)"
            strokeWidth="1"
          />
        ))}

        <path d={area} fill="url(#preenchimentoRecebido)" />
        <path
          d={linha}
          fill="none"
          stroke="var(--color-recebido)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {pontos.map((p, i) => {
          const ehUltimo = i === ultimo;
          return (
            <g key={p.mes}>
              <circle
                cx={x(i)}
                cy={y(p.recebido)}
                r={ehUltimo ? 4.5 : 3}
                fill={ehUltimo ? 'var(--color-recebido)' : 'var(--superficie-elevada)'}
                stroke="var(--color-recebido)"
                strokeWidth="2"
              />
              {/* Só o mês corrente carrega rótulo de valor. Rotular os seis
                  transformaria o gráfico numa tabela mal diagramada.
                  O rótulo fica ACIMA e à ESQUERDA do ponto: à direita ele
                  encostaria na borda, e sobre o ponto o marcador o encobre. */}
              {ehUltimo && (
                <text
                  x={x(i) - 8}
                  y={y(p.recebido) - 14}
                  textAnchor="end"
                  className="tabular"
                  fontSize="12"
                  fontWeight="600"
                  fill="var(--color-recebido)"
                >
                  {moedaCompacta(p.recebido)}
                </text>
              )}
              <text
                x={x(i)}
                y={A - 12}
                textAnchor="middle"
                fontSize="11"
                fill="var(--texto-tenue)"
              >
                {mesCurto(p.mes)}
              </text>
            </g>
          );
        })}
      </svg>

      <figcaption className="mt-3 flex items-center gap-4 text-xs text-[color:var(--texto-suave)]">
        <span className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="h-0.5 w-4 rounded"
            style={{ background: 'var(--color-recebido)' }}
          />
          Recuperado no mês
        </span>
        <span className="text-[color:var(--texto-tenue)]">
          Honorários do escritório no período:{' '}
          <strong className="tabular font-semibold text-[color:var(--acento)]">
            {moeda(pontos.reduce((s, p) => s + p.honorarios, 0))}
          </strong>
        </span>
      </figcaption>
    </figure>
  );
}
